import axios from "axios";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import { useMemo, useRef, useEffect } from "react";
import { AUTH_REFRESH_FAILED_EVENT } from "../Refresh";
import { usePublicSession } from "./context/PublicSessionContext";

export const useAxiosInstance = () => {
  const authHeader = useAuthHeader();
  const signOut = useSignOut();
  const publicSession = usePublicSession();
  const authHeaderRef = useRef(authHeader);
  const signOutRef = useRef(signOut);
  const publicSessionRef = useRef(publicSession);

  useEffect(() => {
    authHeaderRef.current = authHeader;
  }, [authHeader]);

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    publicSessionRef.current = publicSession;
  }, [publicSession]);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_BASE_URL,
      timeout: 30000,
      withCredentials: true,
    });

    instance.interceptors.request.use(
      (config) => {
        if (config.url?.includes("/User/Login")) {
          return config;
        }

        // Public-session tokens carry a "pt_" prefix (see PublicTokenService).
        // This context is only populated inside /public-link/:token (PublicLink.jsx),
        // so it is never active on authenticated routes.
        const rawPublicToken = publicSessionRef.current?.token;
        const publicToken = rawPublicToken?.startsWith("pt_")
          ? rawPublicToken
          : null;
        const token = publicToken
          ? `Bearer ${publicToken}`
          : authHeaderRef.current;

        if (token) {
          config.headers.Authorization = token;
        } else {
          console.warn("Interceptor - No token available for:", config.url);
        }

        return config;
      },
      (error) => {
        console.error("Interceptor - Request error:", error);
        return Promise.reject(error);
      },
    );

    instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        console.error("Interceptor - Response error:", {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
        });

        // Check for CORS or network errors
        if (!error.response && error.message) {
          console.error("Network/CORS Error:", error.message);
        }

        // Do not redirect on 401 when using a public token — the public link
        // page handles the invalid-token state on its own.
        if (publicSessionRef.current?.token?.startsWith("pt_")) {
          return Promise.reject(error);
        }

        // Redirect to sign-in when access token is expired/invalid
        const url = error.config?.url || "";
        if (
          error.response?.status === 401 &&
          !url.includes("/User/Login") &&
          !url.includes("/User/refresh-token")
        ) {
          window.dispatchEvent(new CustomEvent(AUTH_REFRESH_FAILED_EVENT));
          return new Promise(() => {}); // Suppress error propagation while redirecting
        }

        return Promise.reject(error);
      },
    );

    return instance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Instance created once, token read dynamically from ref

  return api;
};
